import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { AccountProfile } from "../../../api/account/types";

/**
 * H-3a end-user account state. Logged-in is DERIVED from profile presence —
 * there is no separate boolean flag.
 */
export interface AccountState {
  profile: AccountProfile | null;
  /**
   * Email captured on the login step for the verify step. Flow state only —
   * the email never goes into the URL (app security rule).
   */
  loginEmail: string | null;
  /**
   * True once the stored-session check has resolved (profile loaded, or no /
   * invalid session). Guards (RequireAccount) wait for this instead of
   * redirecting while GET /me is still in flight on a deep link.
   */
  sessionChecked: boolean;
}

const initialState: AccountState = {
  profile: null,
  loginEmail: null,
  sessionChecked: false,
};

const accountSlice = createSlice({
  name: "accountSlice",
  initialState,
  reducers: {
    setAccountProfile: (state, action: PayloadAction<AccountProfile>) => {
      state.profile = action.payload;
      state.sessionChecked = true;
    },
    /**
     * The STORED session resolving at app start (`useAccountSessionInit`) —
     * the same state change as `setAccountProfile`, under its own name so the
     * account cache listener can tell it apart (#2793). That token is already
     * on every request, so the caches hold its identity's answers and must not
     * be evicted. A real login keeps using `setAccountProfile`, which evicts.
     */
    accountSessionRestored: (state, action: PayloadAction<AccountProfile>) => {
      state.profile = action.payload;
      state.sessionChecked = true;
    },
    setAccountLoginEmail: (state, action: PayloadAction<string>) => {
      state.loginEmail = action.payload;
    },
    clearAccountLoginEmail: (state) => {
      state.loginEmail = null;
    },
    clearAccount: (state) => {
      state.profile = null;
      state.loginEmail = null;
      state.sessionChecked = true;
    },
  },
});

export const {
  setAccountProfile,
  accountSessionRestored,
  setAccountLoginEmail,
  clearAccountLoginEmail,
  clearAccount,
} = accountSlice.actions;

export default accountSlice.reducer;
